import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
} from "react";
// Assuming Select is not needed for a simple School name input, but keeping it if you plan to extend it.
// import Select from "react-select";
import Table from "../../../ui/Table"; // Placeholder for your Table component
import Modal from "../../../ui/Modal"; // Placeholder for your Modal component
import Loader from "../../../ui/Loader"; // Placeholder for your Loader component
import { AuthContext } from "../../../../context/AuthContext";
import {
  swalConfirm,
  swalError,
  swalSuccess,
  swalWarning,
} from "../../../../utils/swalAlert"; // Placeholder for your swal utils
import { fetchWithAuth } from "../../../../utils/api"; // Placeholder for your API utility

// --- School Management Component ---
const ManageSchools = React.forwardRef((props, ref) => {
  const { user } = React.useContext(AuthContext); // Assuming user has an instituteId
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // New state for School
  const [newSchool, setNewSchool] = useState({
    name: "",
  });

  const [editSchool, setEditSchool] = useState(null);
  const [initialEditSchoolName, setInitialEditSchoolName] = useState(null);
  const [isEditChanged, setIsEditChanged] = useState(false);

  const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

  const columns = [
    { key: "name", label: "School Name", sortable: true},
    { key: "edit", label: "", sortable: false },
    { key: "delete", label: "", sortable: false },
  ];

  // --- API Fetching Functions ---

  const fetchSchools = useCallback(async () => {
    try {
      setLoading(true);
      // Assuming a similar API structure for fetching schools by institute
      const response = await fetchWithAuth(
        `${serverUrl}/api/school/get-all/${user.instituteId}`,
        {
          credentials: "include",
        }
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch schools");
      const data = await response.json();
      setSchools(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user.instituteId, serverUrl]);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  // --- Modal Handlers ---

  const openModal = () => {
    setNewSchool({ name: "" });
    setIsCreateModalOpen(true);
  };

  useImperativeHandle(ref, () => ({
    openModal,
  }));

  // --- Create School Handlers ---

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSchool((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateSchool = async (e) => {
    e.preventDefault();
    if (!newSchool.name.trim()) {
      swalError("Please enter a School Name.");
      return;
    }

    setIsCreating(true);
    try {
      const schoolToCreate = {
        name: newSchool.name,
        instituteId: user.instituteId,
      };

      const response = await fetchWithAuth(`${serverUrl}/api/school/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(schoolToCreate),
        credentials: "include",
      });
      if (!response) return;
      if (!response.ok) {
        let errorText;
        try {
          errorText = await response.json();
        } catch {
          errorText = await response.text();
        }
        throw new Error(
          errorText.message || "Failed to create school. Please try again."
        );
      }
      await fetchSchools();
      setIsCreateModalOpen(false);
      swalSuccess("School created successfully!");
    } catch (error) {
     swalError((error.message?.match(/name:\s*(.*)/)?.[1] || ""));
    } finally {
      setIsCreating(false);
    }
  };

  // --- Edit School Handlers ---

  const handleEditClick = (school) => {
    setEditSchool({
      ...school,
      name: school.name || "",
    });
    setInitialEditSchoolName(school.name || "");
    setIsEditModalOpen(true);
    setIsEditChanged(false);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditSchool((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (editSchool && initialEditSchoolName !== null) {
      const isChanged = editSchool.name !== initialEditSchoolName;
      setIsEditChanged(isChanged);
    } else {
      setIsEditChanged(false);
    }
  }, [editSchool, initialEditSchoolName]);

  const handleEditSchool = async (e) => {
    e.preventDefault();

    if (!editSchool.name.trim()) {
      swalError("School name cannot be empty.");
      return;
    }

    if (!isEditChanged) {
      swalWarning("No changes detected.");
      return;
    }

    setIsUpdating(true);
    try {
      const schoolToUpdate = {
        name: editSchool.name,
      };

      const response = await fetchWithAuth(
        `${serverUrl}/api/school/update/${editSchool.id || editSchool._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(schoolToUpdate),
          credentials: "include",
        }
      );
      if (!response) return;
      if (!response.ok) {
        let errorText;
        try {
          errorText = await response.json();
        } catch {
          errorText = await response.text();
        }
        throw new Error(
          errorText.message || "Failed to edit school. Please try again."
        );
      }
      await fetchSchools();
      setEditSchool(null);
      setInitialEditSchoolName(null);
      setIsEditModalOpen(false);
      setIsEditChanged(false);
      swalSuccess("School edited successfully!");
    } catch (error) {
      swalError("Failed to edit school! " + (error.message || ""));
    } finally {
      setIsUpdating(false);
    }
  };

  // --- Delete School Handler ---

  const handleDeleteSchool = async (schoolId) => {
    const result = await swalConfirm(
      "Are you sure you want to delete this school?",
      "This action is permanent and cannot be undone.",
      "Yes, delete it!",
      "No, Cancel"
    );

    if (result.isConfirmed) {
      setDeletingId(schoolId);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/school/delete/${schoolId}`,
          {
            method: "DELETE",
            credentials: "include",
          }
        );
        if (!response) return;
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to delete school.");
        }
        setSchools((prevSchools) =>
          prevSchools.filter((school) => (school.id || school._id) !== schoolId)
        );
        swalSuccess("School deleted successfully!");
      } catch (err) {
        swalError(err.message || "Failed to delete school.");
      } finally {
        setDeletingId(null);
      }
    }
  };

  // --- Data Enhancement and Rendering ---

  const enhancedSchools = schools.map((school) => {
    const schoolId = school._id || school.id;
    return {
      ...school,
      edit: (
        <button
          className="edit-btn"
          onClick={() => handleEditClick(school)}
          disabled={isUpdating || deletingId !== null}
        >
          <i className="fa-solid fa-pen-to-square"></i>
        </button>
      ),
      delete: (
        <button
          className="delete-btn"
          onClick={() => handleDeleteSchool(schoolId)}
          disabled={isUpdating || deletingId !== null}
        >
          {deletingId === schoolId ? (
            <i className="fa-solid fa-spinner fa-spin"></i>
          ) : (
            <i className="fa-solid fa-trash-can"></i>
          )}
        </button>
      ),
    };
  });

  // --- Component Render ---

  return (
    <div>
      <div className="header">
        <button
          className="create-btn"
          onClick={() => setIsCreateModalOpen(true)}
        >
          Add School + 
        </button>
      </div>
      <div className="manage-container">
        {loading && <Loader />}
        {error && <div className="error-message">{error}</div>}
        {!loading && !error && (
          <Table data={enhancedSchools} columns={columns} caption="Schools" />
        )}
      </div>

      {/* --- Create School Modal --- */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => !isCreating && setIsCreateModalOpen(false)}
      >
        <h2>Create New School</h2>
        <form onSubmit={handleCreateSchool}>
          <div className="form-group">
            <label htmlFor="schoolName">
              <i className="fa-solid fa-asterisk requiredIcon"></i> School Name
            </label>
            <input
              type="text"
              id="schoolName"
              name="name"
              value={newSchool.name}
              onChange={handleInputChange}
              required
              disabled={isCreating}
            />
          </div>
          {isCreating ? (
            <Loader />
          ) : (
            <button type="submit" className="btn-submit">
              Create School
            </button>
          )}
        </form>
      </Modal>

      {/* --- Edit School Modal --- */}
      {editSchool && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            if (!isUpdating) {
              setIsEditModalOpen(false);
              setEditSchool(null);
              setInitialEditSchoolName(null);
              setIsEditChanged(false);
            }
          }}
        >
          <h2>Edit School</h2>
          <form onSubmit={handleEditSchool}>
            <div className="form-group">
              <label htmlFor="schoolName">
                <i className="fa-solid fa-asterisk requiredIcon"></i> School Name
              </label>
              <input
                type="text"
                id="schoolName"
                name="name"
                value={editSchool.name || ""}
                onChange={handleEditInputChange}
                required
                disabled={isUpdating}
              />
            </div>
            {isUpdating ? (
              <Loader />
            ) : (
              <button
                type="submit"
                className="btn-submit"
                disabled={!isEditChanged || isUpdating}
              >
                Save
              </button>
            )}
          </form>
        </Modal>
      )}
    </div>
  );
});

export default ManageSchools;